import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { api } from '@/services/api';
import { toast } from 'sonner';

const ServicesSection = ({ onBookingClick }) => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  // Servicios dinámicos desde el backend
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  // Imagenes mapeadas por nombre de servicio
  const serviceImages = {
    'Mauma Shizen - Head Spa': 'https://customer-assets.emergentagent.com/job_headspa-booking/artifacts/h92rsgky_shizen2.jpg',
    'Mauma Lumiere - head Spa': 'https://images.unsplash.com/photo-1560750588-73207b1ef5b8?w=800&q=80',
    'Mauma Select - Head Spa': 'https://customer-assets.emergentagent.com/job_headspa-booking/artifacts/gvrn567u_select.jpg',
    'Masajes Terapeúticos': 'https://customer-assets.emergentagent.com/job_headspa-booking/artifacts/6qalzfih_masajes.jpg'
  };

  // Obtener servicios del backend
  useEffect(() => {
    const fetchServices = async () => {
      try {
        // Usar endpoint público
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || 'https://headspa-booking.preview.emergentagent.com'}/api/public/services`);
        const servicesData = await response.json();
        
        // Mapear servicios con imágenes
        const servicesWithDetails = servicesData.map(service => ({
          id: service.id,
          name: service.name,
          price: service.price,
          currency: service.currency,
          duration: service.duration_minutes,
          description: service.description || 'Experiencia de bienestar',
          color: '#C9A86A',
          gradient: 'from-black/40 to-transparent', // Agregar gradient consistente
          image: serviceImages[service.name] || 'https://images.unsplash.com/photo-1540555700478-4be289fbecef?w=800',
          features: service.description ? service.description.split('\n').filter(f => f.trim()) : [
            'Experiencia personalizada',
            'Ambiente de total relax',
            'Atención profesional'
          ]
        }));
        
        setServices(servicesWithDetails);
      } catch (error) {
        console.error('Error al cargar servicios:', error);
        toast.error('Error al cargar servicios');
      } finally {
        setLoading(false);
      }
    };

    fetchServices();
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const cardVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 12
      }
    }
  };

  return (
    <section className="relative py-20 px-6 bg-[#0a0a0a] overflow-hidden">
      {/* Background decorative elements */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-bl from-[#C9A86A]/10 to-transparent rounded-full blur-3xl" />
      <div className="absolute bottom-0 left-0 w-96 h-96 bg-gradient-to-tr from-[#6B7F5C]/10 to-transparent rounded-full blur-3xl" />

      <motion.div
        ref={ref}
        className="relative max-w-7xl mx-auto"
        initial="hidden"
        animate={inView ? "visible" : "hidden"}
        variants={containerVariants}
      >
        {/* Section header */}
        <motion.div className="text-center mb-16" variants={cardVariants}>
          <motion.h2
            className="text-5xl md:text-6xl font-light mb-4"
            initial={{ scale: 0.8, opacity: 0 }}
            animate={inView ? { scale: 1, opacity: 1 } : {}}
            transition={{ duration: 0.6 }}
          >
            <span className="text-[#C9A86A] font-medium">
              Nuestras
            </span>{' '}
            <span className="text-white">Experiencias</span>
          </motion.h2>
          <motion.p
            className="text-xl text-gray-400 max-w-2xl mx-auto"
            initial={{ y: 20, opacity: 0 }}
            animate={inView ? { y: 0, opacity: 1 } : {}}
            transition={{ delay: 0.2, duration: 0.6 }}
          >
            Cada servicio está diseñado para ofrecerte un momento único de relax y renovación
          </motion.p>
        </motion.div>

        {/* Services grid */}
        <motion.div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6"
          variants={containerVariants}
        >
          {loading ? (
            // Loading state
            <div className="col-span-full text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-[#C9A86A] border-t-transparent"></div>
              <p className="text-gray-400 mt-4">Cargando servicios...</p>
            </div>
          ) : services.length === 0 ? (
            // No services state
            <div className="col-span-full text-center py-12">
              <p className="text-gray-400">No hay servicios disponibles en este momento</p>
            </div>
          ) : (
            // Services list
            services.map((service, index) =>
            <motion.div
              key={index}
              className="group relative"
              variants={cardVariants}
              whileHover={{ y: -10 }}
              transition={{ duration: 0.3 }}
            >
              {/* Card */}
              <div className="relative h-full bg-[#1a1a1a] rounded-3xl overflow-hidden shadow-xl border border-white/10 flex flex-col">
                {/* Image with overlay */}
                <div className="relative h-48 overflow-hidden">
                  <motion.img
                    src={service.image}
                    alt={service.name}
                    className="w-full h-full object-cover"
                    whileHover={{ scale: 1.1 }}
                    transition={{ duration: 0.6 }}
                  />
                  <div className={`absolute inset-0 bg-gradient-to-t ${service.gradient} opacity-30`} />
                  
                  {/* Price badge */}
                  <motion.div
                    className="absolute top-4 left-4 bg-white/95 backdrop-blur-sm px-4 py-2 rounded-full shadow-lg"
                    initial={{ scale: 0, rotate: -180 }}
                    animate={inView ? { scale: 1, rotate: 0 } : {}}
                    transition={{ delay: 0.3 + index * 0.1, type: "spring" }}
                  >
                    <span className="text-2xl font-bold" style={{ color: service.color }}>
                      ${service.price.toLocaleString()}
                    </span>
                  </motion.div>
                </div>

                {/* Content */}
                <div className="p-6 flex flex-col h-full">
                  {/* Info section - crece para empujar el botón al final */}
                  <div className="flex-grow space-y-4">
                    {/* Title */}
                    <div>
                      <h3 className="text-2xl font-medium mb-1" style={{ color: service.color }}>
                        {service.name}
                      </h3>
                      <p className="text-sm text-gray-400">{service.description}</p>
                    </div>

                    {/* Duration */}
                    <div className="flex items-center gap-2 text-gray-400">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span className="text-sm">{service.duration} minutos</span>
                    </div>

                    {/* Features */}
                    <ul className="space-y-2">
                      {service.features.map((feature, fIndex) => (
                        <motion.li
                          key={fIndex}
                          className="flex items-start gap-2 text-sm text-gray-300"
                          initial={{ x: -10, opacity: 0 }}
                          animate={inView ? { x: 0, opacity: 1 } : {}}
                          transition={{ delay: 0.4 + index * 0.1 + fIndex * 0.05 }}
                        >
                          <svg className={`w-4 h-4 mt-0.5 text-[#C9A86A] flex-shrink-0`} fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          <span>{feature}</span>
                        </motion.li>
                      ))}
                    </ul>
                  </div>

                  {/* Book button - siempre al final */}
                  <motion.button
                    onClick={onBookingClick}
                    className="w-full mt-4 py-3 text-white font-medium rounded-full shadow-lg relative overflow-hidden group"
                    style={{ backgroundColor: service.color }}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <span className="relative z-10">Reservar</span>
                    <motion.div
                      className="absolute inset-0 bg-white"
                      initial={{ x: '-100%' }}
                      whileHover={{ x: '100%' }}
                      transition={{ duration: 0.5 }}
                      style={{ opacity: 0.2 }}
                    />
                  </motion.button>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
};

export default ServicesSection;
